"use client";

import React from "react";
import { Target } from "lucide-react";
import { SafeHTMLRenderer } from "@utils/homeDataLoader";
import SeoChip from "@components/ui/SeoChip";

export interface ValuePropositionBadgeProps {
  seoChip: string;
  titleLine1: string;
  titleLine2?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
  headingLevel?: "h2" | "h3" | "p";
}

export default function ValuePropositionBadge({
  seoChip,
  titleLine1,
  titleLine2,
  description,
  icon: Icon = Target,
  className = "",
  headingLevel = "h2",
}: ValuePropositionBadgeProps) {
  const HeadingTag = headingLevel;
  // Match home page section-header font sizes: 1.625rem (26px) mobile, 2rem (32px) tablet, 2.75rem (44px) desktop
  const headingSizeClass = headingLevel === "h3"
    ? "text-2xl md:text-3xl"
    : headingLevel === "p"
    ? "text-2xl md:text-3xl font-bold"
    : "section-header";

  // Check if we're on a dark background (className contains "text-white")
  const isDarkBackground = className.includes("text-white");
  // Check if alignment is specified in className
  const hasTextLeft = className.includes("text-left");
  const hasTextRight = className.includes("text-right");
  const hasTextCenter = className.includes("text-center");
  // Only apply text-center if no alignment class is specified
  const alignmentClass = hasTextLeft || hasTextRight || hasTextCenter ? "" : "text-center";
  
  const titleLine1Class = isDarkBackground ? "text-white" : "text-abis-primary";
  const titleLine2Class = "text-abis-accent";
  const descriptionClass = isDarkBackground ? "text-white/90" : "text-gray-600";
  // Adjust description margin based on alignment
  const descriptionMarginClass = hasTextLeft ? "mx-0" : "mx-auto";

  return (
    <div className={`${alignmentClass} ${className}`}>
      <SeoChip
        text={seoChip}
        icon={Icon}
        variant={isDarkBackground ? "dark" : "light"}
      />

      {/* Title: single flowing sentence; line breaks via max-width + text-balance (no forced <br>). */}
      <HeadingTag
        className={`${headingSizeClass} font-bold mb-6 max-w-5xl mx-auto text-balance [text-wrap:balance]`}
      >
        <span className={titleLine1Class}>{titleLine1}</span>
        {titleLine2 ? (
          <>
            {" "}
            <span className={titleLine2Class}>{titleLine2}</span>
          </>
        ) : null}
      </HeadingTag>

      {/* Description */}
      {description && (
        <p className={`text-lg ${descriptionClass} max-w-4xl ${descriptionMarginClass} leading-relaxed`}>
          <SafeHTMLRenderer content={description} />
        </p>
      )}
    </div>
  );
}

